import { format, parse, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, subQuarters } from 'date-fns';
import { Calendar, Play, Globe, MessageSquare } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import DatePicker from '../DatePicker';
import type { PancakeActiveView } from '../../types/pancake';

interface Preset {
  label: string;
  from: Date;
  to: Date;
}

const viewTabs: { key: PancakeActiveView; label: string }[] = [
  { key: 'overview', label: 'Tổng quan' },
  { key: 'conversations', label: 'Hội thoại' },
  { key: 'statistics', label: 'Thống kê' },
  { key: 'customers', label: 'Khách hàng' },
  { key: 'posts', label: 'Bài viết' },
];

function platformBadge(platform: string) {
  const p = platform?.toLowerCase() || '';
  if (p.includes('facebook') || p === 'fb') {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300"><Globe className="w-3 h-3" />FB</span>;
  }
  if (p.includes('zalo')) {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-300"><MessageSquare className="w-3 h-3" />Zalo</span>;
  }
  if (p.includes('instagram') || p === 'ig') {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-500/20 text-pink-300"><Globe className="w-3 h-3" />IG</span>;
  }
  return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-500/20 text-slate-300">{platform}</span>;
}

export { platformBadge };

export default function PancakeDashboardFilters({ isDark }: { isDark: boolean }) {
  const {
    fromDate,
    toDate,
    activeView,
    selectedPageId,
    pages,
    setDateRange,
    setActiveView,
    setSelectedPageId,
    fetchDashboardData,
  } = usePancakeStore();

  const fromDateObj = parse(fromDate, 'yyyy-MM-dd', new Date());
  const toDateObj = parse(toDate, 'yyyy-MM-dd', new Date());

  const now = new Date();

  // Week presets
  const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
  const thisSunday = endOfWeek(now, { weekStartsOn: 1 });
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });

  const weekPresets: Preset[] = [
    { label: 'Tuần rồi', from: lastMonday, to: lastSunday },
    { label: 'Tuần này', from: thisMonday, to: thisSunday },
  ];

  // Month presets
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const monthNames = ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'];
  const specificMonthPresets: Preset[] = [];
  for (let m = 0; m <= currentMonth; m++) {
    const d = new Date(currentYear, m, 1);
    specificMonthPresets.push({
      label: monthNames[m],
      from: startOfMonth(d),
      to: endOfMonth(d),
    });
  }

  // Quarter presets
  const quarterPresets: Preset[] = [
    { label: 'Quý trước', from: startOfQuarter(subQuarters(now, 1)), to: endOfQuarter(subQuarters(now, 1)) },
    { label: 'Quý này', from: startOfQuarter(now), to: endOfQuarter(now) },
  ];

  const isPresetSelected = (preset: Preset) =>
    fromDate === format(preset.from, 'yyyy-MM-dd') &&
    toDate === format(preset.to, 'yyyy-MM-dd');

  const selectPreset = (preset: Preset) => {
    setDateRange(format(preset.from, 'yyyy-MM-dd'), format(preset.to, 'yyyy-MM-dd'));
  };

  const handleFromChange = (date: Date | null) => {
    if (!date) return;
    setDateRange(format(date, 'yyyy-MM-dd'), toDate);
  };

  const handleToChange = (date: Date | null) => {
    if (!date) return;
    setDateRange(fromDate, format(date, 'yyyy-MM-dd'));
  };

  const handleViewChange = (view: PancakeActiveView) => {
    setActiveView(view);
    setTimeout(() => usePancakeStore.getState().fetchDashboardData(), 0);
  };

  const presetBtnClass = (active: boolean) =>
    `px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
      active
        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
        : isDark
          ? 'bg-white/[0.03] border-white/10 text-white/40 hover:text-white hover:bg-white/[0.06]'
          : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`;

  const separatorClass = `w-px h-4 ${isDark ? 'bg-white/10' : 'bg-gray-300'}`;

  return (
    <div className={`rounded-lg p-3 sm:p-4 space-y-3 ${isDark ? 'bg-white/[0.02] border border-white/[0.06]' : 'bg-white border border-gray-200 shadow-sm'}`}>
      {/* Row 1: Date pickers + Page selector + Run button */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
        <div className="flex gap-3 sm:gap-4" style={{ overflow: 'visible' }}>
          <div className="flex-1 sm:flex-none" style={{ overflow: 'visible' }}>
            <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Từ ngày</label>
            <div className="relative" style={{ overflow: 'visible' }}>
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <DatePicker
                selected={fromDateObj}
                onChange={handleFromChange}
                isDark={isDark}
                className={`pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-44 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
                placeholderText="Chọn ngày bắt đầu"
              />
            </div>
          </div>
          <div className="flex-1 sm:flex-none" style={{ overflow: 'visible' }}>
            <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Đến ngày</label>
            <div className="relative" style={{ overflow: 'visible' }}>
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <DatePicker
                selected={toDateObj}
                onChange={handleToChange}
                isDark={isDark}
                className={`pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-44 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
                placeholderText="Chọn ngày kết thúc"
              />
            </div>
          </div>
        </div>

        {/* Page selector */}
        <div className="flex-1 sm:flex-none">
          <label className={`text-xs uppercase tracking-wider block mb-1.5 sm:mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Trang</label>
          <select
            value={selectedPageId}
            onChange={(e) => setSelectedPageId(e.target.value as string)}
            className={`px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-full sm:w-56 ${isDark ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50' : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500'}`}
          >
            <option value="all">Tất cả trang</option>
            {pages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.name} ({page.platform})
              </option>
            ))}
          </select>
        </div>

        <div className="sm:self-end sm:pb-0.5">
          <button
            onClick={() => fetchDashboardData(true)}
            className="px-5 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-all bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/25 hover:-translate-y-0.5"
          >
            <Play className="w-4 h-4" />
            Chạy report
          </button>
        </div>
      </div>

      {/* Row 2: Presets */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs mr-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Chọn nhanh:</span>

        {weekPresets.map((p) => (
          <button key={p.label} onClick={() => selectPreset(p)} className={presetBtnClass(isPresetSelected(p))}>
            {p.label}
          </button>
        ))}

        <div className={separatorClass} />

        {specificMonthPresets.map((p) => (
          <button key={p.label} onClick={() => selectPreset(p)} className={presetBtnClass(isPresetSelected(p))}>
            {p.label}
          </button>
        ))}

        <div className={separatorClass} />

        {quarterPresets.map((p) => (
          <button key={p.label} onClick={() => selectPreset(p)} className={presetBtnClass(isPresetSelected(p))}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Row 3: View Tabs */}
      <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
        {viewTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleViewChange(tab.key)}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
              activeView === tab.key
                ? 'bg-cyan-500 text-white'
                : isDark
                  ? 'bg-white/[0.03] text-white/40 hover:text-white hover:bg-white/[0.06]'
                  : 'bg-gray-50 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}
